import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr, fmtDate, formatApiError, API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, FileDown, Eye, Paperclip, Pencil, X, ScanSearch, CheckCircle2, AlertTriangle, Link2 } from "lucide-react";
import { toast } from "sonner";

const MODES = ["NEFT", "RTGS", "IMPS", "UPI", "Cheque", "Cash", "Bank Transfer"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const monthLabel = (m) => `${MONTH_NAMES[parseInt(m.slice(5), 10) - 1]} ${m.slice(0, 4)}`;
const today = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLE = {
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Partial: "bg-amber-50 text-amber-700 border-amber-200",
  Unpaid: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function VendorBillsPage() {
  const { vid } = useParams();
  const badVid = !vid || vid === "undefined" || vid === "null";
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("all");
  const [sel, setSel] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [form, setForm] = useState({ amount: "", date: today(), mode: "NEFT", reference: "", notes: "" });
  const [vendorPos, setVendorPos] = useState([]);
  const [editBill, setEditBill] = useState(null);
  const [linkPo, setLinkPo] = useState("");

  const unlinkPo = async (b, pn) => {
    if (!window.confirm(`Unlink ${pn} from bill ${b.number}?`)) return;
    try {
      await api.patch(`/vendors/bills/${b.id}/unlink-po?po_number=${encodeURIComponent(pn)}`);
      toast.success(`${pn} unlinked from ${b.number}`);
      const r = await api.get(`/vendors/${vid}/bills-detail`);
      setData(r.data);
      setEditBill((eb) => (eb ? r.data.bills.find((x) => x.id === eb.id) : null));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setPo = async () => {
    if (!editBill || !linkPo) return;
    try {
      const res = await api.patch(`/vendors/bills/${editBill.id}/set-po?po_number=${encodeURIComponent(linkPo)}`);
      toast.success(res.data.clubbed
        ? `${editBill.number} clubbed with ${linkPo} — amount auto-split by PO value (adjust below if needed)`
        : `${editBill.number} linked to ${linkPo}`);
      setLinkPo("");
      const r = await api.get(`/vendors/${vid}/bills-detail`);
      setData(r.data);
      setEditBill(r.data.bills.find((x) => x.id === editBill.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [splitRows, setSplitRows] = useState([]);
  useEffect(() => {
    if (!editBill) { setSplitRows([]); return; }
    const allocs = editBill.allocations || [];
    setSplitRows((editBill.po_numbers || []).map((pn) => ({
      po_number: pn,
      amount: allocs.find((a) => a.po_number === pn)?.amount ?? "",
    })));
  }, [editBill]);
  const splitSum = +splitRows.reduce((s, r) => s + (+r.amount || 0), 0).toFixed(2);
  const splitOk = editBill ? Math.abs(splitSum - (+editBill.amount || 0)) <= 0.01 : false;

  const saveSplit = async () => {
    if (!editBill) return;
    try {
      await api.patch(`/vendors/bills/${editBill.id}/allocations`, {
        allocations: splitRows.map((r) => ({ po_number: r.po_number, amount: +r.amount || 0 })),
      });
      toast.success(`Split saved for bill ${editBill.number}`);
      const r = await api.get(`/vendors/${vid}/bills-detail`);
      setData(r.data);
      setEditBill(r.data.bills.find((x) => x.id === editBill.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [fixBill, setFixBill] = useState(null);
  const [fixForm, setFixForm] = useState({ number: "", date: "", amount: "" });
  const openFix = (b) => { setFixBill(b); setFixForm({ number: b.number, date: b.date, amount: String(b.amount) }); };
  const saveFix = async () => {
    if (!(+fixForm.amount > 0)) { toast.error("Enter a valid amount"); return; }
    try {
      await api.patch(`/vendors/bills/${fixBill.id}`, { number: fixForm.number, date: fixForm.date, amount: +fixForm.amount });
      toast.success(`Bill ${fixForm.number} corrected`);
      setFixBill(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [audit, setAudit] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [fixing, setFixing] = useState("");
  const startAudit = async () => {
    setAuditOpen(true);
    try {
      const r = await api.post(`/vendors/${vid}/bills/audit`);
      pollAudit(r.data.audit_id);
    } catch (e) { toast.error(formatApiError(e)); setAuditOpen(false); }
  };
  const pollAudit = async (aid) => {
    try {
      const r = await api.get(`/vendors/bills/audit/${aid}`);
      setAudit(r.data);
      if (r.data.status === "running") setTimeout(() => pollAudit(aid), 2500);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const fixToDoc = async (res) => {
    setFixing(res.bill_id);
    try {
      await api.patch(`/vendors/bills/${res.bill_id}`, { amount: res.document_amount });
      toast.success(`Bill ${res.number} corrected to ${inr(res.document_amount)}`);
      setAudit((a) => ({ ...a, results: a.results.map((x) => (x.bill_id === res.bill_id ? { ...x, fixed: true } : x)) }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setFixing("");
  };

  const attachFile = async (b, f) => {
    if (!f) return;
    setUploading(b.id);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/vendors/bills/${b.id}/attach-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Bill file attached to ${b.number}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading("");
  };

  const load = () => {
    if (badVid) return;
    api.get(`/vendors/${vid}/bills-detail`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get(`/vendors/${vid}/open-pos`).then((r) => setVendorPos(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [vid]);

  const months = useMemo(() => [...new Set((data?.bills || []).map((b) => b.date.slice(0, 7)).filter((m) => m.length === 7))].sort().reverse(), [data]);
  const [query, setQuery] = useState("");
  const bills = useMemo(() => (data?.bills || []).filter((b) => {
    if (month !== "all" && b.date.slice(0, 7) !== month) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (b.number || "").toLowerCase().includes(q) || b.po_numbers.some((p) => p.toLowerCase().includes(q));
  }), [data, month, query]);
  const grouped = useMemo(() => {
    const g = {};
    bills.forEach((b) => { (g[b.date.slice(0, 7)] = g[b.date.slice(0, 7)] || []).push(b); });
    return Object.entries(g).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [bills]);

  const selected = bills.filter((b) => sel[b.id]);
  if (badVid) {
    return (
      <div className="py-16 text-center space-y-3" data-testid="vendor-bills-invalid">
        <p className="text-sm text-slate-500">No vendor selected. If this keeps happening, refresh the page to load the latest app version.</p>
        <Link to="/payments/payables" className="text-sm font-semibold text-blue-600 hover:underline">← Back to Payables</Link>
      </div>
    );
  }
  const selectedDue = +selected.reduce((s, b) => s + b.due, 0).toFixed(2);
  const toggle = (b) => setSel((s) => ({ ...s, [b.id]: !s[b.id] }));

  const openPay = () => {
    setForm({ amount: selectedDue || "", date: today(), mode: "NEFT", reference: "", notes: "" });
    setOpen(true);
  };

  const pay = async () => {
    if (!(+form.amount > 0)) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post(`/vendors/${vid}/payments`, {
        amount: +form.amount, date: form.date, mode: form.mode, reference: form.reference, notes: form.notes,
        bill_numbers: selected.map((b) => b.number).filter(Boolean),
      });
      toast.success(selected.length
        ? `Payment recorded against ${selected.length} bill${selected.length === 1 ? "" : "s"}`
        : "Payment recorded — settles oldest bills first");
      setOpen(false);
      setSel({});
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const row = (b) => (
    <tr key={b.id} className={`border-t border-slate-100 hover:bg-slate-50/50 ${b.overdue ? "bg-rose-50/40" : ""}`} data-testid={`vbill-row-${b.id}`}>
      <td className="px-3 py-2.5">
        {b.due > 0.01 ? (
          <Checkbox checked={!!sel[b.id]} onCheckedChange={() => toggle(b)} data-testid={`vbill-check-${b.id}`} />
        ) : <span className="inline-block w-4" />}
      </td>
      <td className="px-3 py-2.5 font-mono text-xs text-slate-800">
        <span className="inline-flex items-center gap-1">
          {b.number || "—"}
          <button onClick={() => openFix(b)} title="Edit bill (number / date / amount)"
                  className="p-0.5 text-slate-300 hover:text-blue-600" data-testid={`vbill-edit-${b.id}`}>
            <Pencil size={11} />
          </button>
        </span>
      </td>
      <td className="px-3 py-2.5 text-slate-600 text-xs">{fmtDate(b.date)}</td>
      <td className="px-3 py-2.5 font-mono text-xs text-slate-600">
        <span className="inline-flex items-center gap-1 flex-wrap">
          {b.po_numbers.length > 1 && (
            <span className="font-sans inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1 py-0.5" title={`One bill clubbed across ${b.po_numbers.length} purchase orders`} data-testid={`vbill-clubbed-${b.id}`}>
              <Link2 size={9} /> Clubbed ·{b.po_numbers.length}
            </span>
          )}
          {b.po_numbers.join(", ") || "—"}
          <button onClick={() => { setEditBill(b); setLinkPo(""); }} title="Edit PO links"
                  className="p-0.5 text-slate-300 hover:text-blue-600" data-testid={`vbill-editpo-${b.id}`}>
            <Pencil size={11} />
          </button>
        </span>
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums font-medium">{inr(b.amount)}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{b.po_amount === null ? "—" : inr(b.po_amount)}</td>
      <td className={`px-3 py-2.5 text-right tabular-nums ${b.difference ? (b.difference > 0 ? "text-rose-600 font-semibold" : "text-emerald-700 font-semibold") : "text-slate-400"}`}>
        {b.difference === null ? "—" : (Math.abs(b.difference) <= 0.01 ? "0" : inr(b.difference))}
      </td>
      <td className={`px-3 py-2.5 text-xs ${b.overdue ? "text-rose-600 font-semibold" : "text-slate-600"}`}>{b.due_date ? fmtDate(b.due_date) : "—"}{b.overdue ? " · overdue" : ""}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{b.due > 0.01 ? <span className="text-rose-600 font-medium">{inr(b.due)}</span> : <span className="text-slate-400">—</span>}</td>
      <td className="px-3 py-2.5 text-center">
        <span className={`text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 ${STATUS_STYLE[b.status]}`}>{b.status}</span>
      </td>
      <td className="px-3 py-2.5 text-right">
        {b.file_id ? (
          <a href={`${API_URL}/files/${b.file_id}/download`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50 transition-colors duration-150"
             title="Open the uploaded bill" data-testid={`vbill-view-${b.id}`}>
            <Eye size={12} /> View
          </a>
        ) : (
          <label className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-blue-700 border border-dashed border-slate-300 rounded-md px-2 py-1 hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer transition-colors duration-150"
                 title="Upload the bill file (PDF or photo)" data-testid={`vbill-attach-${b.id}`}>
            <Paperclip size={11} /> {uploading === b.id ? "Uploading…" : "Add file"}
            <input type="file" accept=".pdf,image/*" className="hidden" disabled={uploading === b.id}
                   onChange={(e) => { attachFile(b, e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        )}
      </td>
    </tr>
  );

  return (
    <div className="space-y-4" data-testid="vendor-bills-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/payments/payables" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-1" data-testid="vbills-back">
            <ArrowLeft size={12} /> Payables
          </Link>
          <h1 className="font-display text-2xl font-semibold text-slate-900">{data?.vendor?.name || "Vendor"} — Bills</h1>
          <p className="text-sm text-slate-500">
            Total billed <b>{inr(data?.total_billed || 0)}</b> · Outstanding <b className="text-rose-600">{inr(data?.total_due || 0)}</b> · credit period {data?.vendor?.credit_days ?? 60} days
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search bill no / PO no"
                 className="w-56 h-10" data-testid="vbills-search" />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-44 h-10" data-testid="vbills-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={startAudit}
                  className="inline-flex items-center gap-1.5 h-10 px-3 text-xs font-semibold border border-amber-300 bg-amber-50 rounded-md hover:bg-amber-100 text-amber-800" data-testid="vbills-audit">
            <ScanSearch size={13} /> Audit Bills
          </button>
          <a href={`${API_URL}/vendors/${vid}/ledger/pdf`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 h-10 px-3 text-xs font-semibold border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700" data-testid="vbills-stmt">
            <FileDown size={13} /> Statement
          </a>
        </div>
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60">
                <th className="px-3 py-2.5 w-8"></th>
                <th className="px-3 py-2.5">Bill No</th>
                <th className="px-3 py-2.5">Date</th>
                <th className="px-3 py-2.5">PO No</th>
                <th className="px-3 py-2.5 text-right">Bill Amt</th>
                <th className="px-3 py-2.5 text-right">PO Amt</th>
                <th className="px-3 py-2.5 text-right">Diff</th>
                <th className="px-3 py-2.5">Due Date</th>
                <th className="px-3 py-2.5 text-right">Balance</th>
                <th className="px-3 py-2.5 text-center">Status</th>
                <th className="px-3 py-2.5 text-right">Bill</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map(([m, rows]) => (
                [
                  month === "all" && (
                    <tr key={`h-${m}`} className="bg-slate-50/80 border-t border-slate-100" data-testid={`vbills-month-${m}`}>
                      <td colSpan={11} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                        {monthLabel(m)} · {rows.length} bill{rows.length === 1 ? "" : "s"} · {inr(rows.reduce((s, b) => s + b.amount, 0))}
                      </td>
                    </tr>
                  ),
                  ...rows.map(row),
                ]
              ))}
              {data && bills.length === 0 && <tr><td colSpan={11} className="text-center py-12 text-sm text-slate-500">No bills {month === "all" ? "yet" : "in this month"}.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {selected.length > 0 && (
        <div className="sticky bottom-3 z-20 flex items-center justify-between gap-3 bg-slate-900 text-white rounded-xl px-4 py-3 shadow-xl" data-testid="vbills-paybar">
          <span className="text-sm">{selected.length} bill{selected.length === 1 ? "" : "s"} selected · balance <b>{inr(selectedDue)}</b></span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSel({})} className="text-xs text-slate-300 hover:text-white" data-testid="vbills-clear-sel">Clear</button>
            <Button onClick={openPay} size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="vbills-pay-selected">Pay Selected</Button>
          </div>
        </div>
      )}

      <Dialog open={!!editBill} onOpenChange={(o) => !o && setEditBill(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>PO Links — Bill {editBill?.number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Linked purchase orders</Label>
              <div className="mt-2 space-y-1.5" data-testid="editpo-current">
                {(editBill?.po_numbers || []).length === 0 && <p className="text-xs text-slate-400">Not linked to any PO (on account).</p>}
                {(editBill?.po_numbers || []).map((pn) => (
                  <div key={pn} className="flex items-center justify-between gap-2 text-xs border border-slate-200 rounded-md px-2.5 py-1.5">
                    <span className="font-mono flex-1">{pn}</span>
                    {(editBill?.po_numbers || []).length > 1 && (
                      <Input type="number" value={splitRows.find((r) => r.po_number === pn)?.amount ?? ""}
                             onChange={(e) => setSplitRows((rows) => rows.map((r) => (r.po_number === pn ? { ...r, amount: e.target.value } : r)))}
                             className="h-7 w-28 text-xs" placeholder="Amount" data-testid={`editpo-amt-${pn}`} />
                    )}
                    <button onClick={() => unlinkPo(editBill, pn)} className="p-1 text-slate-400 hover:text-rose-600" title="Unlink" data-testid={`editpo-unlink-${pn}`}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
                {(editBill?.po_numbers || []).length > 1 && (
                  <div className="flex items-center justify-between pt-1">
                    <span className={`text-[11px] font-medium ${splitOk ? "text-emerald-600" : "text-rose-600"}`} data-testid="editpo-split-sum">
                      Split {inr(splitSum)} / Bill {inr(editBill?.amount || 0)}{splitOk ? " ✓" : ""}
                    </span>
                    <Button size="sm" variant="outline" onClick={saveSplit} disabled={!splitOk} className="h-7 text-xs" data-testid="editpo-save-split">Save Split</Button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Link to a PO of this vendor</Label>
              <div className="flex gap-2 mt-2">
                <Select value={linkPo} onValueChange={setLinkPo}>
                  <SelectTrigger className="h-10 flex-1" data-testid="editpo-select"><SelectValue placeholder="Select PO" /></SelectTrigger>
                  <SelectContent>
                    {vendorPos.map((p) => (
                      <SelectItem key={p.number} value={p.number}>{p.number} · {inr(p.total)} · {fmtDate(p.date)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={setPo} disabled={!linkPo} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="editpo-link">Link</Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Only this vendor's POs can be linked. Linking a 2nd PO clubs them — the bill amount is auto-split by PO value (adjust above). Unlinking also clears the bill tag on the Purchase Orders page.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fixBill} onOpenChange={(o) => !o && setFixBill(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Bill {fixBill?.number}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Bill No</Label>
              <Input value={fixForm.number} onChange={(e) => setFixForm({ ...fixForm, number: e.target.value })} className="mt-1.5 h-10" data-testid="editbill-number" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Date</Label>
              <Input type="date" value={fixForm.date} onChange={(e) => setFixForm({ ...fixForm, date: e.target.value })} className="mt-1.5 h-10" data-testid="editbill-date" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">Bill Amount</Label>
              <Input type="number" value={fixForm.amount} onChange={(e) => setFixForm({ ...fixForm, amount: e.target.value })} className="mt-1.5 h-10" data-testid="editbill-amount" />
              {fixBill && (fixBill.po_numbers || []).length > 1 && (
                <p className="text-[11px] text-slate-500 mt-1.5">This bill is clubbed across {fixBill.po_numbers.length} POs — the split is rescaled proportionally when you change the amount.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixBill(null)}>Cancel</Button>
            <Button onClick={saveFix} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="editbill-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={auditOpen} onOpenChange={(o) => { setAuditOpen(o); if (!o) setAudit(null); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanSearch size={16} /> Bill Audit — {data?.vendor?.name}</DialogTitle></DialogHeader>
          {!audit && <p className="text-sm text-slate-500 py-4" data-testid="audit-starting">Starting audit…</p>}
          {audit && (
            <div className="space-y-3" data-testid="audit-body">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>
                  {audit.status === "running" ? `Re-reading bill files with AI… ${audit.done}/${audit.total}` : `Checked ${audit.done} bill${audit.done === 1 ? "" : "s"} with files`}
                  {audit.skipped_no_file > 0 && <span className="text-slate-400"> · {audit.skipped_no_file} without a file skipped</span>}
                </span>
                {audit.status === "running" && <span className="animate-pulse text-amber-600 font-medium">Running…</span>}
              </div>
              {audit.total > 0 && (
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${(audit.done / audit.total) * 100}%` }} />
                </div>
              )}
              <div className="max-h-[50vh] overflow-y-auto space-y-1.5">
                {(audit.results || []).filter((r) => r.status === "mismatch" && !r.fixed).map((r) => (
                  <div key={r.bill_id} className="flex items-center justify-between gap-3 border border-rose-200 bg-rose-50/50 rounded-md px-3 py-2" data-testid={`audit-mismatch-${r.number}`}>
                    <div className="text-xs">
                      <p className="font-semibold text-slate-800 flex items-center gap-1.5"><AlertTriangle size={12} className="text-rose-600" /> Bill {r.number} · {fmtDate(r.date)}</p>
                      <p className="text-slate-600 mt-0.5">CRM shows <b className="text-rose-700">{inr(r.stored_amount)}</b> but the bill document says <b className="text-emerald-700">{inr(r.document_amount)}</b> (off by {inr(Math.abs(r.diff))})</p>
                      {r.note && <p className="text-[10px] text-slate-400 mt-0.5">{r.note}</p>}
                    </div>
                    <Button size="sm" onClick={() => fixToDoc(r)} disabled={fixing === r.bill_id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 h-8 text-xs" data-testid={`audit-fix-${r.number}`}>
                      {fixing === r.bill_id ? "Fixing…" : `Fix to ${inr(r.document_amount)}`}
                    </Button>
                  </div>
                ))}
                {(audit.results || []).filter((r) => r.fixed).map((r) => (
                  <div key={r.bill_id} className="flex items-center gap-2 border border-emerald-200 bg-emerald-50/50 rounded-md px-3 py-2 text-xs text-emerald-800" data-testid={`audit-fixed-${r.number}`}>
                    <CheckCircle2 size={13} /> Bill {r.number} corrected to {inr(r.document_amount)}
                  </div>
                ))}
                {(audit.results || []).filter((r) => r.status === "unreadable").map((r) => (
                  <div key={r.bill_id} className="border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-500" data-testid={`audit-unreadable-${r.number}`}>
                    Bill {r.number} — could not verify: {r.note || "file unreadable"}
                  </div>
                ))}
                {audit.status === "done" && (audit.results || []).every((r) => r.status === "ok") && audit.total > 0 && (
                  <p className="text-sm text-emerald-700 flex items-center gap-1.5 py-2" data-testid="audit-all-ok"><CheckCircle2 size={15} /> All bill amounts match their documents.</p>
                )}
                {audit.status === "done" && audit.total === 0 && (
                  <p className="text-sm text-slate-500 py-2">No bills with attached files to audit — attach bill files first, then re-run.</p>
                )}
                {(audit.results || []).filter((r) => r.status === "ok").length > 0 && (
                  <p className="text-[11px] text-slate-400 pt-1">{(audit.results || []).filter((r) => r.status === "ok").length} bill(s) verified OK ✓</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Pay {data?.vendor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {selected.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-xs text-slate-700 space-y-1" data-testid="vbills-pay-list">
                {selected.map((b) => (
                  <div key={b.id} className="flex justify-between"><span className="font-mono">{b.number}</span><span className="tabular-nums">{inr(b.due)}</span></div>
                ))}
                <div className="flex justify-between font-semibold border-t border-slate-200 pt-1"><span>Total balance</span><span className="tabular-nums">{inr(selectedDue)}</span></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Amount *</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-11" data-testid="vbills-pay-amount" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-11" data-testid="vbills-pay-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="vbills-pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">UTR / Reference</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5 h-11" data-testid="vbills-pay-reference" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="vbills-pay-notes" />
            </div>
            <p className="text-[11px] text-slate-500">
              {selected.length ? "The payment is applied to the selected bills, oldest first." : "No bills selected — the payment settles this vendor's oldest bills first."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={pay} disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="vbills-pay-save">{saving ? "Saving…" : "Record Payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
